import React from "react";
import PropTypes from "prop-types";

export const SectionBlock = ({ title, children, className = "" }) => (
  <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
    <div className="border-b border-slate-200 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
    </div>
    <div className="px-4 py-4">{children}</div>
  </section>
);

SectionBlock.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export const FieldRow = ({ label, value, valueClassName = "" }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2.5 last:border-b-0">
    <div className="min-w-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</div>
    <div className={`max-w-[65%] text-right text-sm font-medium text-slate-900 ${valueClassName}`}>{value || "-"}</div>
  </div>
);

FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  valueClassName: PropTypes.string,
};

export const SummaryBadge = ({ label, value, tone = "dark" }) => {
  const toneClasses = {
    dark: "border-slate-200 bg-white text-slate-900",
    blue: "border-blue-200 bg-blue-50 text-blue-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
  };

  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClasses[tone] || toneClasses.dark}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-60">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold">{value || "-"}</div>
    </div>
  );
};

SummaryBadge.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  tone: PropTypes.string,
};

const renderHistoryDescription = (description) => {
  const lines = String(description || "No details available.")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  return lines.map((line, index) => {
    const labelMatch = line.match(/^([^:]+:)\s*(.*)$/);

    if (!labelMatch) {
      return (
        <div key={`${line}-${index}`} className="text-[13px] font-medium leading-4.5 text-slate-800">
          {line}
        </div>
      );
    }

    return (
      <div key={`${line}-${index}`} className="text-[13px] leading-4.5 text-slate-700">
        <span className="font-semibold text-slate-900">{labelMatch[1]}</span>{" "}
        <span>{labelMatch[2] || "-"}</span>
      </div>
    );
  });
};

export const HistoryItem = ({ title, assignedTo, timestamp, description }) => (
  <div className="relative pl-3.5">
    <div className="absolute left-1 top-1 h-full w-px bg-slate-200" />
    <div className="absolute left-0.5 top-1.5 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-blue-100" />
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-bold leading-4 text-slate-900">{title || "Follow up"}</div>
          {assignedTo && <div className="mt-0.5 text-[11px] font-semibold leading-4 text-blue-700">{assignedTo}</div>}
        </div>
        <div className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          {timestamp || "-"}
        </div>
      </div>
      <div className="mt-1.5 space-y-1">
        {renderHistoryDescription(description)}
      </div>
    </div>
  </div>
);

HistoryItem.propTypes = {
  title: PropTypes.string,
  assignedTo: PropTypes.string,
  timestamp: PropTypes.string,
  description: PropTypes.string,
};
